// ==========================================================================
// screens/FamilyTree.tsx — the family tree (FF-057, 10 Oct 2026)
//   James: "a really cool interactive way to build out a family tree with
//   lines and boxes and relations then set it and its a visual image of a tree
//   or a box and line style (maybe you can set the display style in the edit)
//   … looks like a really cool family tree and you can add to it and build it
//   out over the years."
//   Two drawings of the same data, chosen for the whole household:
//     Tree   a real tree: the youngest at the trunk, ancestors up in the
//            branches, names in handwriting, leaves along the boughs.
//     Chart  boxes and lines: couples joined, children hanging off a bar.
//   Tap anyone to see their people and add a parent, partner, child or
//   sibling. Drag to move around, scroll or the buttons to zoom, "Save as a
//   picture" for a PNG (a wallpaper or something to print).
//   v2 (10 Oct): ChatGPT's Draft_v1 pack merged (bloodline rings and colours,
//   tracing ancestors and descendants, keyboard focus, safer export), then the
//   artwork redrawn toward the concept James picked (familyTreeArtwork.tsx).
//   On a computer the person's details sit in a panel beside the tree; on the
//   phone they slide up as before. The saved picture carries its own fonts.
// ==========================================================================
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as RPE } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Download, Maximize2, Minus, Network, Pencil, Plus, Trash2, TreeDeciduous, UserPlus, X } from "lucide-react";
import caveat700 from "@fontsource/caveat/files/caveat-latin-700-normal.woff2?url";
import cinzel600 from "@fontsource/cinzel/files/cinzel-latin-600-normal.woff2?url";
import jakarta400 from "@fontsource/plus-jakarta-sans/files/plus-jakarta-sans-latin-400-normal.woff2?url";
import jakarta600 from "@fontsource/plus-jakarta-sans/files/plus-jakarta-sans-latin-600-normal.woff2?url";
import Sheet, { Lbl, inputCls } from "@/components/Sheet";
import { ErrorText } from "@/components/ui";
import { ChartArtwork, TreeArtwork, ART_DIM, initialsOf, lineColour } from "./familyTreeArtwork";
import { calculateBloodlines, compactLayout, traceFamily, FAMILY_PALETTE, type Bloodlines } from "@/lib/familyTreeLineage";
import { usePeople, type Person } from "@/lib/household";
import {
  fullName, isGone, layoutTree, useRemoveTreePerson, useSaveTreePerson, useSetBranchColour, useSetTreeStyle, useStartTree, useTree, useUpdateLink, years,
  type PartnerStatus, type PersonDraft, type Relation, type TreeLink, type TreePerson, type TreeStyle,
} from "@/lib/tree";

// ---------- colours, resolved to plain values so a saved picture keeps them ----------
function useColours() {
  const [v, setV] = useState(0);
  useEffect(() => { const m = window.matchMedia("(prefers-color-scheme: dark)"); const f = () => setV((x) => x + 1); m.addEventListener("change", f); const o = new MutationObserver(f); o.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "data-mode"] }); return () => { m.removeEventListener("change", f); o.disconnect(); }; }, []);
  return useMemo(() => {
    const cs = getComputedStyle(document.documentElement);
    const tok = (n: string, a = 1) => { const raw = cs.getPropertyValue(`--${n}`).trim() || "128 128 128"; return `rgb(${raw.split(/\s+/).join(", ")}${a < 1 ? `, ${a}` : ""})`.replace("rgb(", a < 1 ? "rgba(" : "rgb("); };
    const dark = document.documentElement.dataset.mode === "dark";
    return { tok, dark };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v]);
}
/** A picture made from an SVG can't reach the page's fonts, so the saved PNG
 *  carries them inside (handwriting, the crest serif, the body font). */
let fontCss: Promise<string> | null = null;
function embeddedFonts(): Promise<string> {
  if (!fontCss) {
    const faces: [string, string, number][] = [["Caveat", caveat700, 700], ["Cinzel", cinzel600, 600], ["Plus Jakarta Sans", jakarta400, 400], ["Plus Jakarta Sans", jakarta600, 600]];
    fontCss = Promise.all(faces.map(async ([family, url, weight]) => {
      const buf = await (await fetch(url)).arrayBuffer();
      let bin = ""; const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      return `@font-face{font-family:"${family}";font-weight:${weight};src:url(data:font/woff2;base64,${btoa(bin)}) format("woff2");}`;
    })).then((x) => x.join("")).catch(() => "");
  }
  return fontCss;
}

/** On a computer the person's panel sits beside the tree. */
function useWide() {
  const q = "(min-width: 1024px)";
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => { const m = window.matchMedia(q); const f = () => setWide(m.matches); m.addEventListener("change", f); return () => m.removeEventListener("change", f); }, []);
  return wide;
}

// Stable empty arrays avoid recalculating the layout on every hover / zoom.
const EMPTY_PEOPLE: TreePerson[] = [];
const EMPTY_LINKS: TreeLink[] = [];

export default function FamilyTree() {
  const tree = useTree(); const people = usePeople(); const setStyle = useSetTreeStyle(); const start = useStartTree();
  const C = useColours();
  const style: TreeStyle = tree.data?.style ?? "tree";
  const all = tree.data?.people ?? EMPTY_PEOPLE;
  const links = tree.data?.links ?? EMPTY_LINKS;
  const L = useMemo(() => compactLayout(layoutTree(all, links)), [all, links]);
  const bloodlines = useMemo(() => calculateBloodlines(all, links, L), [all, links, L]);
  const [sel, setSel] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const focusId = hovered ?? sel;
  const trace = useMemo(() => traceFamily(links, focusId), [links, focusId]);
  const [showLegend, setShowLegend] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [adding, setAdding] = useState<{ rel?: Relation; edit?: TreePerson } | null>(null);
  const appPerson = (p: TreePerson) => people.data?.find((x) => x.id === p.person_id);

  // geometry
  const D = ART_DIM[style];
  const W = Math.max(1, L.width) * D.sw + D.padX * 2;
  const H = Math.max(1, L.gens) * D.rh + D.padY * 2 + D.extra;
  const px = useCallback((id: string) => D.padX + (L.nodes.get(id)?.x ?? 0) * D.sw + D.sw / 2, [D, L]);
  const py = useCallback((id: string) => {
    const g = L.nodes.get(id)?.gen ?? 0;
    return D.padY + g * D.rh + D.rh / 2;
  }, [D, L]);

  // pan and zoom
  const box = useRef<HTMLDivElement>(null); const svg = useRef<SVGSVGElement>(null);
  const [t, setT] = useState({ x: 0, y: 0, k: 1 });
  const drag = useRef<{ x: number; y: number; tx: number; ty: number; moved: boolean; hit: string | null } | null>(null);
  const fit = useCallback(() => {
    const el = box.current; if (!el) return;
    const cw = el.clientWidth, ch = el.clientHeight;
    const k = Math.min(cw / W, ch / H, 1.15);
    setT({ k, x: (cw - W * k) / 2, y: (ch - H * k) / 2 });
  }, [W, H]);
  // Fit when the genealogy changes OR when a sheet/sidebar changes canvas size.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    fit();
    return () => observer.disconnect();
  }, [fit, style]);
  useEffect(() => {
    const el = box.current; if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect(); const mx = e.clientX - r.left, my = e.clientY - r.top;
      setT((o) => { const k = Math.min(3, Math.max(0.2, o.k * (e.deltaY < 0 ? 1.12 : 1 / 1.12))); return { k, x: mx - ((mx - o.x) * k) / o.k, y: my - ((my - o.y) * k) / o.k }; });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);
  const zoom = (f: number) => { const el = box.current; if (!el) return; const mx = el.clientWidth / 2, my = el.clientHeight / 2; setT((o) => { const k = Math.min(3, Math.max(0.2, o.k * f)); return { k, x: mx - ((mx - o.x) * k) / o.k, y: my - ((my - o.y) * k) / o.k }; }); };
  // Two fingers on a phone: pinch to zoom.
  const pts = useRef(new Map<number, { x: number; y: number }>()); const pinch = useRef<{ d: number; k: number; x: number; y: number; mx: number; my: number } | null>(null);
  const pinchStart = () => {
    const [a, b] = [...pts.current.values()]; const el = box.current; if (!a || !b || !el) return;
    const r = el.getBoundingClientRect();
    pinch.current = { d: Math.hypot(a.x - b.x, a.y - b.y), k: t.k, x: t.x, y: t.y, mx: (a.x + b.x) / 2 - r.left, my: (a.y + b.y) / 2 - r.top };
    drag.current = null;
  };
  const pinchMove = () => {
    const p0 = pinch.current; const [a, b] = [...pts.current.values()]; if (!p0 || !a || !b) return;
    const k = Math.min(3, Math.max(0.2, p0.k * (Math.hypot(a.x - b.x, a.y - b.y) / p0.d)));
    setT({ k, x: p0.mx - ((p0.mx - p0.x) * k) / p0.k, y: p0.my - ((p0.my - p0.y) * k) / p0.k });
  };
  const down = (e: RPE) => {
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pts.current.size === 2) { pinchStart(); return; } drag.current = { x: e.clientX, y: e.clientY, tx: t.x, ty: t.y, moved: false, hit: (e.target as Element).closest("[data-person]")?.getAttribute("data-person") ?? null }; (e.currentTarget as Element).setPointerCapture?.(e.pointerId); };
  const move = (e: RPE) => {
    if (pts.current.has(e.pointerId)) pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current) { pinchMove(); return; }
    const d = drag.current; if (!d) return; const dx = e.clientX - d.x, dy = e.clientY - d.y; if (Math.abs(dx) + Math.abs(dy) > 4) d.moved = true; if (d.moved) setT((o) => ({ ...o, x: d.tx + dx, y: d.ty + dy })); };
  const up = (e: RPE) => {
    pts.current.delete(e.pointerId);
    if (pinch.current) { if (pts.current.size < 2) pinch.current = null; return; }
    const d = drag.current; drag.current = null;
    void e; if (d && !d.moved) setSel(d.hit);
  };

  const savePicture = async () => {
    const el = svg.current;
    if (!el) return;
    setExportError(null);
    try {
      const clone = el.cloneNode(true) as SVGSVGElement;
      const multiplier = Math.min(2, 8192 / W, 8192 / H);
      const outW = Math.max(1, Math.round(W * multiplier));
      const outH = Math.max(1, Math.round(H * multiplier));
      clone.setAttribute("viewBox", `0 0 ${W} ${H}`);
      clone.setAttribute("width", String(outW));
      clone.setAttribute("height", String(outH));
      clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      clone.querySelector("[data-pan]")?.setAttribute("transform", "");
      // Restore every branch and hide selected-node halos for the keepsake.
      clone.querySelectorAll("[data-trace-opacity]").forEach((n) => n.setAttribute("opacity", "1"));
      clone.querySelectorAll("[data-selection-halo], [data-trace-highlight]").forEach((n) => n.remove());
      const css = await embeddedFonts();
      if (css) { const st = document.createElementNS("http://www.w3.org/2000/svg", "style"); st.textContent = css; clone.insertBefore(st, clone.firstChild); }
      const xml = new XMLSerializer().serializeToString(clone);
      const svgUrl = URL.createObjectURL(new Blob([xml], { type: "image/svg+xml;charset=utf-8" }));
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = outW; canvas.height = outH;
          const context = canvas.getContext("2d");
          if (!context) throw new Error("Canvas is not available on this device.");
          context.fillStyle = C.tok("tree-paper");
          context.fillRect(0, 0, outW, outH);
          context.drawImage(img, 0, 0, outW, outH);
          canvas.toBlob((blob) => {
            if (!blob) { setExportError("Couldn't create a picture on this device."); return; }
            const pngUrl = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = pngUrl; anchor.download = "our-family-tree.png";
            document.body.appendChild(anchor); anchor.click(); anchor.remove();
            setTimeout(() => URL.revokeObjectURL(pngUrl), 30_000);
          }, "image/png");
        } catch (error) {
          setExportError(error instanceof Error ? error.message : "The picture could not be saved.");
        } finally { URL.revokeObjectURL(svgUrl); }
      };
      img.onerror = () => { URL.revokeObjectURL(svgUrl); setExportError("This device couldn't render the picture."); };
      img.src = svgUrl;
    } catch (error) {
      setExportError(error instanceof Error ? error.message : "The picture could not be saved.");
    }
  };

  const selected = all.find((p) => p.id === sel) ?? null;
  const wide = useWide();
  const panelBeside = wide && !!selected;
  // The canvas is as tall as the drawing wants at this width (capped to the window), not the whole window.
  const [boxW, setBoxW] = useState(0);
  useEffect(() => { const el = box.current; if (!el) return; const ro = new ResizeObserver(() => setBoxW(el.clientWidth)); ro.observe(el); setBoxW(el.clientWidth); return () => ro.disconnect(); }, [all.length]);
  const winH = typeof window !== "undefined" ? window.innerHeight : 900;
  const boxH = Math.round(Math.max(440, Math.min(winH - (wide ? 230 : 300), boxW ? boxW * (H / W) * (panelBeside ? 1.15 : 1) : 560)));

  return (
    <>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/family" className="mb-2 inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"><ArrowLeft className="h-4 w-4" />Family</Link>
          <h1 className="font-heading text-3xl font-semibold leading-tight lg:text-[40px]">Our family tree</h1>
          <p className="mt-1 text-[15px] text-ink-muted">{all.length ? `${all.length} people across ${L.gens} generation${L.gens === 1 ? "" : "s"}. Tap anyone to see their story.` : "Start with the three of you and grow it over the years."}</p>
        </div>
        {all.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-full bg-surface p-1 shadow-soft ring-1 ring-line/70" role="group" aria-label="How it's drawn">
              <button type="button" onClick={() => setStyle.mutate("tree")} aria-pressed={style === "tree"} className={`flex min-h-10 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm ${style === "tree" ? "bg-primary font-semibold text-primary-ink" : "text-ink-muted"}`}><TreeDeciduous className="h-4 w-4" />Tree</button>
              <button type="button" onClick={() => setStyle.mutate("chart")} aria-pressed={style === "chart"} className={`flex min-h-10 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm ${style === "chart" ? "bg-primary font-semibold text-primary-ink" : "text-ink-muted"}`}><Network className="h-4 w-4" />Chart</button>
            </div>
            <button type="button" onClick={savePicture} className="flex min-h-10 items-center gap-1.5 rounded-full bg-surface px-4 py-2 text-sm font-semibold shadow-soft ring-1 ring-line/70 hover:ring-primary/40"><Download className="h-4 w-4" />Save as a picture</button>
            <button type="button" onClick={() => setAdding({})} className="flex min-h-10 items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-ink shadow-soft"><UserPlus className="h-4 w-4" />Add someone</button>
          </div>
        )}
      </div>

      {exportError && <p role="alert" className="mb-3 text-sm text-bad">{exportError}</p>}
      {tree.isLoading && <p className="text-sm text-ink-muted">Growing the tree…</p>}
      {!tree.isLoading && all.length === 0 && (
        <div className="relative overflow-hidden rounded-3xl px-6 py-16 text-center shadow-soft ring-1 ring-line/70" style={{ background: `radial-gradient(ellipse at 50% 110%, ${C.tok("chart-3", 0.18)}, transparent 60%), ${C.tok("surface")}` }}>
          <TreeDeciduous className="mx-auto h-16 w-16" style={{ color: C.tok("chart-3") }} strokeWidth={1.25} />
          <p className="mt-4 font-hand text-5xl" style={{ color: C.tok("chart-3") }}>Plant the tree</p>
          <p className="mx-auto mt-2 max-w-md text-[15px] text-ink-muted">Start with the people in this household and branch out: parents, grandparents, aunties, cousins. Add a few at a time over the years.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <button type="button" onClick={() => start.mutate()} disabled={start.isPending || !people.data?.length} className="rounded-full bg-primary px-5 py-3 font-semibold text-primary-ink disabled:opacity-50">Start with {people.data?.map((p) => p.display_name).join(", ") || "us"}</button>
            <button type="button" onClick={() => setAdding({})} className="rounded-full bg-surface-raised px-5 py-3 font-semibold">Start from scratch</button>
          </div>
          <ErrorText>{start.error?.message}</ErrorText>
        </div>
      )}

      {all.length > 0 && (
        <div className={`grid gap-5 ${panelBeside ? "lg:grid-cols-[minmax(0,1fr)_380px]" : ""}`}>
        <div ref={box} className="relative min-h-[440px] touch-none select-none overflow-hidden rounded-3xl shadow-soft ring-1 ring-line/70"
          style={{ background: C.tok("tree-paper"), height: boxH }}>
          <svg ref={svg} width="100%" height="100%" className="family-tree-visual cursor-grab active:cursor-grabbing"
            onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
            onPointerLeave={() => setHovered(null)} role="group" aria-label="Interactive family tree">
            <g data-pan transform={`translate(${t.x} ${t.y}) scale(${t.k})`}>
              {style === "tree"
                ? <TreeArtwork L={L} people={all} links={links} bloodlines={bloodlines} trace={trace}
                    px={px} py={py} W={W} H={H} C={C} sel={sel} focusId={focusId}
                    scale={t.k} onPick={setSel} onHover={setHovered} />
                : <ChartArtwork L={L} people={all} links={links} bloodlines={bloodlines} trace={trace}
                    px={px} py={py} W={W} H={H} C={C} sel={sel} focusId={focusId}
                    scale={t.k} onPick={setSel} onHover={setHovered} />}
            </g>
          </svg>
          <div className="absolute left-3 top-3 z-10 sm:left-5 sm:top-5">
            <button type="button" onClick={() => setShowLegend((value) => !value)} aria-expanded={showLegend}
              className="rounded-full border border-line bg-surface/95 px-4 py-2 text-xs font-semibold text-ink shadow-soft backdrop-blur">
              🌿 Bloodlines {showLegend ? "▴" : "▾"}
            </button>
            {showLegend && <div className="mt-2 max-h-[50vh] w-64 overflow-auto rounded-2xl border border-line bg-surface p-3 shadow-soft">
              <p className="mb-2 text-xs font-semibold text-ink">Family branches</p>
              {bloodlines.roots.map(({person, paletteIndex}) => <button type="button" key={person.id}
                onClick={() => { setSel(person.id); setShowLegend(false); }}
                className="flex w-full min-h-10 items-center gap-3 rounded-lg px-2 py-1 text-left hover:bg-surface-raised">
                <span className="h-0 w-9 shrink-0 border-t-[4px]"
                  style={{ borderTopColor: C.tok(FAMILY_PALETTE[paletteIndex].token), borderTopStyle: FAMILY_PALETTE[paletteIndex].dash ? "dashed" : "solid" }} />
                <span className="text-xs text-ink">{fullName(person)}</span>
              </button>)}
              <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">Each person who starts a branch gets a colour, and everyone under them carries it in their ring. Tap a person at the top of a branch to pick their colour.</p>
            </div>}
          </div>
          <div className="absolute bottom-4 right-4 flex flex-col overflow-hidden rounded-2xl bg-surface shadow-soft ring-1 ring-line/70">
            <button type="button" onClick={() => zoom(1.2)} aria-label="Zoom in" className="p-3.5 hover:bg-surface-raised"><Plus className="h-4 w-4" /></button>
            <button type="button" onClick={() => zoom(1 / 1.2)} aria-label="Zoom out" className="border-t border-line p-3.5 hover:bg-surface-raised"><Minus className="h-4 w-4" /></button>
            <button type="button" onClick={fit} aria-label="Fit it all in" className="border-t border-line p-3.5 hover:bg-surface-raised"><Maximize2 className="h-4 w-4" /></button>
          </div>
          <div className="pointer-events-none absolute bottom-4 left-4 hidden items-center gap-3 rounded-2xl bg-surface/90 px-4 py-2.5 text-xs text-ink-muted shadow-soft ring-1 ring-line/70 backdrop-blur sm:flex">
            <span className="text-lg">👆</span><span><span className="block font-semibold text-ink">Drag to move around</span>Scroll to zoom · Tap a person to see their story</span>
          </div>
        </div>
        {panelBeside && selected && (
          <aside className="self-start rounded-3xl bg-surface p-6 shadow-soft ring-1 ring-line/70">
            <div className="mb-1 flex justify-end"><button type="button" onClick={() => setSel(null)} aria-label="Close" className="rounded-full p-1.5 text-ink-muted hover:bg-surface-raised hover:text-ink"><X className="h-5 w-5" /></button></div>
            <PersonDetails p={selected} all={all} links={links} app={appPerson(selected)} bloodlines={bloodlines} C={C} onClose={() => setSel(null)} onPick={setSel}
              onAdd={(rel) => setAdding({ rel })} onEdit={() => setAdding({ edit: selected })} />
          </aside>
        )}
        </div>
      )}

      {selected && !adding && !panelBeside && <PersonSheet p={selected} all={all} links={links} app={appPerson(selected)} bloodlines={bloodlines} C={C} onClose={() => setSel(null)} onPick={setSel}
        onAdd={(rel) => setAdding({ rel })} onEdit={() => setAdding({ edit: selected })} />}
      {adding && <AddSheet rel={adding.rel} edit={adding.edit} all={all} links={links} onClose={() => setAdding(null)} onSaved={(id) => { setAdding(null); setSel(id); }} />}
    </>
  );
}

// ---------- One person and their people ----------
type DetailsProps = {
  p: TreePerson; all: TreePerson[]; links: TreeLink[]; app?: Person; bloodlines: Bloodlines;
  C: ReturnType<typeof useColours>; onClose: () => void; onPick: (id: string) => void;
  onAdd: (r: Relation) => void; onEdit: () => void;
};

/** On the phone: the same details in a sheet that slides up. */
function PersonSheet(props: DetailsProps) {
  return <Sheet title={fullName(props.p)} onClose={props.onClose}><PersonDetails {...props} bare /></Sheet>;
}

function PersonDetails({ p, all, links, app, bloodlines, C, onClose, onPick, onAdd, onEdit, bare }: DetailsProps & { bare?: boolean }) {
  const remove = useRemoveTreePerson(); const upd = useUpdateLink(); const setColour = useSetBranchColour();
  const [sure, setSure] = useState(false);
  useEffect(() => setSure(false), [p.id]);
  const by = new Map(all.map((x) => [x.id, x]));
  const lineage = bloodlines.byPerson.get(p.id);
  const founder = bloodlines.roots.find((x) => x.person.id === p.id);
  const used = new Map(bloodlines.roots.filter((x) => x.person.id !== p.id).map((x) => [x.paletteIndex, x.person]));
  const tint = (id: string) => lineColour(C, bloodlines.byPerson.get(id)?.dominant ?? 0);
  const parents = links.filter((l) => l.kind === "parent" && l.to_id === p.id).map((l) => l.from_id);
  const kids = links.filter((l) => l.kind === "parent" && l.from_id === p.id).map((l) => l.to_id);
  const partnerLinks = links.filter((l) => l.kind === "partner" && (l.from_id === p.id || l.to_id === p.id));
  const sibs = [...new Set(links.filter((l) => l.kind === "parent" && parents.includes(l.from_id) && l.to_id !== p.id).map((l) => l.to_id))];
  const onlyPartner = partnerLinks.length === 1 ? (partnerLinks[0].from_id === p.id ? partnerLinks[0].to_id : partnerLinks[0].from_id) : null;
  const loneParent = parents.length === 1 ? parents[0] : null;
  const row = (id: string, extra?: React.ReactNode) => {
    const x = by.get(id); if (!x) return null;
    return <div key={id} className="flex items-center gap-2">
      <button type="button" onClick={() => onPick(id)} className="flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-xl bg-surface-raised/70 px-2.5 py-1.5 text-left text-sm transition hover:bg-primary/10">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-heading text-[11px] font-semibold" style={{ color: tint(id), background: lineColour(C, bloodlines.byPerson.get(id)?.dominant ?? 0, 0.14) }}>{initialsOf(x)}</span>
        <span className="truncate">{fullName(x)}</span>
        {isGone(x) && <span className="ml-auto shrink-0 text-xs text-ink-muted">{years(x)}</span>}
      </button>
      {extra}
    </div>;
  };
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <span className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full font-heading text-3xl font-semibold text-white shadow-soft" style={{ background: tint(p.id) }}>{initialsOf(p)}</span>
        <div className="min-w-0">
          {!bare && <h2 className="font-heading text-2xl font-semibold leading-tight">{fullName(p)}</h2>}
          {p.nickname && <div className="font-hand text-2xl leading-none">“{p.nickname}”</div>}
          <div className="text-sm text-ink-muted">{[years(p), p.birthplace ? `born in ${p.birthplace}` : null].filter(Boolean).join(", ") || "No dates yet"}</div>
          {p.maiden_name && <div className="text-sm text-ink-muted">née {p.maiden_name}</div>}
          {app && <div className="text-xs text-ink-muted">In the app as {app.display_name}</div>}
          {isGone(p) && <div className="font-hand text-lg text-ink-muted">In loving memory</div>}
        </div>
      </div>
      {p.note && <p className="whitespace-pre-line rounded-xl bg-surface-raised p-3 text-sm">{p.note}</p>}

      {([["Parents", parents], ["Brothers and sisters", sibs], ["Children", kids]] as const).map(([label, ids]) => ids.length > 0 && (
        <div key={label}><div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">{label}</div><div className="space-y-1.5">{ids.map((id) => row(id))}</div></div>
      ))}
      {partnerLinks.length > 0 && (
        <div>
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">Partner{partnerLinks.length > 1 ? "s" : ""}</div>
          <div className="space-y-1.5">
            {partnerLinks.map((l) => row(l.from_id === p.id ? l.to_id : l.from_id,
              <select key={l.id} value={l.status ?? "married"} onChange={(e) => upd.mutate({ id: l.id, status: e.target.value as PartnerStatus })} aria-label="Together as" className="min-h-11 rounded-xl border border-line bg-surface px-2 text-sm">
                {(["married", "partners", "separated", "divorced", "widowed"] as const).map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
              </select>))}
          </div>
        </div>
      )}

      {founder && <section className="rounded-2xl border border-line bg-surface-raised/40 p-3" aria-label="Branch colour">
        <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">Branch colour</div>
        <p className="mb-2.5 text-xs text-ink-muted">{p.given_name} starts a branch of the family (no parents in the tree yet). Everyone under {p.given_name} carries this colour.</p>
        <div className="flex flex-wrap gap-2">
          {FAMILY_PALETTE.map((pal, i) => {
            const other = used.get(i);
            const on = founder.paletteIndex === i;
            return <button key={pal.key} type="button" title={other ? `${pal.name}: ${other.given_name}'s branch` : pal.name} aria-pressed={on} disabled={!!other && !on}
              onClick={() => setColour.mutate({ id: p.id, colour: i + 1 })}
              className={`flex h-9 w-9 items-center justify-center rounded-full ring-offset-2 ring-offset-surface transition ${on ? "ring-2 ring-ink" : other ? "opacity-30" : "hover:scale-105"}`}
              style={{ background: C.tok(pal.token) }}>{on && <Check className="h-4 w-4 text-white" />}</button>;
          })}
        </div>
        {p.branch_colour && <button type="button" onClick={() => setColour.mutate({ id: p.id, colour: null })} className="mt-2 text-xs text-ink-muted underline">Let the app choose</button>}
      </section>}
      {lineage && lineage.parts.length > 1 && <section className="rounded-2xl border border-line bg-surface-raised/40 p-3" aria-label="Family branches">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">Family branches</div>
        <div className="flex h-2.5 overflow-hidden rounded-full" role="img" aria-label="Each colour is one recorded family branch">
          {lineage.parts.map((part) => <span key={part.originId} style={{ width: `${part.fraction * 100}%`, minWidth: 2, backgroundColor: C.tok(FAMILY_PALETTE[part.paletteIndex].token) }} />)}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {lineage.parts.map((part) => { const f = by.get(part.originId); return f ? <button type="button" key={part.originId} onClick={() => onPick(f.id)} className="flex min-h-9 items-center gap-1.5 rounded-full bg-surface px-2.5 py-1 text-xs ring-1 ring-line/70 hover:ring-primary/50">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: C.tok(FAMILY_PALETTE[part.paletteIndex].token) }} />{f.nickname || f.given_name}</button> : null; })}
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">From the relationships entered here, not DNA.</p>
      </section>}

      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">Add to the tree</div>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" disabled={parents.length >= 2} onClick={() => onAdd({ kind: "parent", of: p.id, alsoWith: loneParent })} className="rounded-xl bg-surface-raised p-3 text-left text-sm font-semibold disabled:opacity-40">⬆️ A parent</button>
          <button type="button" onClick={() => onAdd({ kind: "partner", of: p.id, status: "married" })} className="rounded-xl bg-surface-raised p-3 text-left text-sm font-semibold">💞 A partner</button>
          <button type="button" onClick={() => onAdd({ kind: "child", of: p.id, alsoWith: onlyPartner })} className="rounded-xl bg-surface-raised p-3 text-left text-sm font-semibold">⬇️ A child</button>
          <button type="button" disabled={!parents.length} title={parents.length ? undefined : "Add a parent first"} onClick={() => onAdd({ kind: "sibling", of: p.id })} className="rounded-xl bg-surface-raised p-3 text-left text-sm font-semibold disabled:opacity-40">↔️ A brother or sister</button>
        </div>
        {!parents.length && <p className="mt-1.5 text-xs text-ink-muted">Brothers and sisters hang off the parents, so add a parent first.</p>}
      </div>

      <div className="flex gap-2">
        <button type="button" onClick={onEdit} className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-primary py-3 font-semibold text-primary-ink shadow-soft"><Pencil className="h-4 w-4" />Edit details</button>
        {sure
          ? <button type="button" onClick={() => remove.mutate({ id: p.id, links }, { onSuccess: onClose })} className="rounded-full bg-bad/10 px-4 py-3 text-sm font-semibold text-bad">Yes, take them out</button>
          : <button type="button" onClick={() => setSure(true)} aria-label="Remove" className="rounded-full bg-surface-raised px-4 py-3 text-bad"><Trash2 className="h-4 w-4" /></button>}
      </div>
      {!bare && <p className="border-t border-line pt-5 text-center font-hand text-2xl leading-tight text-ink-muted">“Family isn’t just where life begins, it’s where it always belongs.”</p>}
    </div>
  );
}

// ---------- Add or edit someone ----------
function AddSheet({ rel, edit, all, links, onClose, onSaved }: { rel?: Relation; edit?: TreePerson; all: TreePerson[]; links: TreeLink[]; onClose: () => void; onSaved: (id: string) => void }) {
  const save = useSaveTreePerson();
  const by = new Map(all.map((x) => [x.id, x]));
  const of = rel ? by.get(rel.of) : undefined;
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [existing, setExisting] = useState("");
  const [f, setF] = useState<PersonDraft>(edit ? { ...edit } : { person_id: null, given_name: "", family_name: rel && rel.kind !== "partner" && of?.family_name ? of.family_name : null, maiden_name: null, nickname: null, born_on: null, born_year: null, died_on: null, died_year: null, birthplace: null, note: null, branch_colour: null });
  const set = (p: Partial<PersonDraft>) => setF({ ...f, ...p });
  const [also, setAlso] = useState(!!rel?.alsoWith);
  const [status, setStatus] = useState<PartnerStatus>(rel?.status ?? "married");
  const [gone, setGone] = useState(!!(edit && isGone(edit)));
  const [err, setErr] = useState<string | null>(null);
  const alsoName = rel?.alsoWith ? fullName(by.get(rel.alsoWith)!) : "";
  const title = edit ? `Edit ${edit.given_name}` : !rel ? "Add someone" : rel.kind === "parent" ? `A parent of ${of?.given_name}` : rel.kind === "partner" ? `${of?.given_name}'s partner` : rel.kind === "child" ? `A child of ${of?.given_name}` : `${of?.given_name}'s brother or sister`;
  const yearOrDate = (v: string, which: "born" | "died") => {
    const s = v.trim();
    if (/^\d{4}$/.test(s)) set(which === "born" ? { born_year: Number(s), born_on: null } : { died_year: Number(s), died_on: null });
    else if (/^\d{4}-\d{2}-\d{2}$/.test(s)) set(which === "born" ? { born_on: s, born_year: null } : { died_on: s, died_year: null });
    else set(which === "born" ? { born_on: null, born_year: null } : { died_on: null, died_year: null });
  };
  const submit = () => {
    const r: Relation | undefined = rel ? { ...rel, status, alsoWith: also ? rel.alsoWith : null } : undefined;
    if (mode === "existing" && rel) {
      if (!existing) { setErr("Pick who."); return; }
      save.mutate({ existingId: existing, rel: r, links }, { onSuccess: onSaved, onError: (e) => setErr(e.message) });
      return;
    }
    if (!f.given_name.trim()) { setErr("A first name at least."); return; }
    const d = gone ? f : { ...f, died_on: null, died_year: null };
    save.mutate({ d, rel: r, links }, { onSuccess: onSaved, onError: (e) => setErr(e.message) });
  };
  const candidates = all.filter((x) => x.id !== rel?.of);
  return (
    <Sheet title={title} onClose={onClose}>
      <div className="space-y-4">
        {rel && !edit && candidates.length > 0 && (
          <div className="flex rounded-full bg-surface-raised p-1 text-sm">
            <button type="button" onClick={() => setMode("new")} className={`flex-1 rounded-full py-1.5 ${mode === "new" ? "bg-surface font-semibold shadow-sm" : "text-ink-muted"}`}>Someone new</button>
            <button type="button" onClick={() => setMode("existing")} className={`flex-1 rounded-full py-1.5 ${mode === "existing" ? "bg-surface font-semibold shadow-sm" : "text-ink-muted"}`}>Already in the tree</button>
          </div>
        )}
        {mode === "existing" && rel ? (
          <Lbl label="Who"><select className={inputCls} value={existing} onChange={(e) => setExisting(e.target.value)}><option value="">Choose…</option>{candidates.map((x) => <option key={x.id} value={x.id}>{fullName(x)}{years(x) ? ` (${years(x)})` : ""}</option>)}</select></Lbl>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Lbl label="First name"><input className={inputCls} value={f.given_name} onChange={(e) => set({ given_name: e.target.value })} autoFocus /></Lbl>
              <Lbl label="Surname"><input className={inputCls} value={f.family_name ?? ""} onChange={(e) => set({ family_name: e.target.value })} /></Lbl>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Lbl label="Known as (optional)"><input className={inputCls} value={f.nickname ?? ""} onChange={(e) => set({ nickname: e.target.value })} placeholder="Nan, Pop, Aunty Sue" /></Lbl>
              <Lbl label="Maiden name (optional)"><input className={inputCls} value={f.maiden_name ?? ""} onChange={(e) => set({ maiden_name: e.target.value })} /></Lbl>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Lbl label="Born" hint="A year is fine, or the full date"><input className={inputCls} defaultValue={f.born_on ?? (f.born_year ? String(f.born_year) : "")} onChange={(e) => yearOrDate(e.target.value, "born")} placeholder="1958 or 1958-04-21" /></Lbl>
              <Lbl label="Born in (optional)"><input className={inputCls} value={f.birthplace ?? ""} onChange={(e) => set({ birthplace: e.target.value })} placeholder="Cork, Ireland" /></Lbl>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={gone} onChange={(e) => setGone(e.target.checked)} className="h-4 w-4" />Passed away</label>
            {gone && <Lbl label="Died" hint="A year is fine"><input className={inputCls} defaultValue={f.died_on ?? (f.died_year ? String(f.died_year) : "")} onChange={(e) => yearOrDate(e.target.value, "died")} placeholder="2015" /></Lbl>}
            <Lbl label="A few words (optional)"><textarea className={`${inputCls} min-h-[70px]`} value={f.note ?? ""} onChange={(e) => set({ note: e.target.value })} placeholder="Ran the corner shop for 30 years, loved the footy" /></Lbl>
          </>
        )}
        {rel?.kind === "partner" && (
          <Lbl label="Together as"><select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value as PartnerStatus)}>{(["married", "partners", "separated", "divorced", "widowed"] as const).map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}</select></Lbl>
        )}
        {rel?.alsoWith && (rel.kind === "child" || rel.kind === "parent") && (
          <label className="flex items-center gap-2 rounded-xl bg-surface-raised p-3 text-sm"><input type="checkbox" checked={also} onChange={(e) => setAlso(e.target.checked)} className="h-4 w-4" />{rel.kind === "child" ? `Also ${alsoName}'s child` : `Partner of ${alsoName}`}</label>
        )}
        <ErrorText>{err}</ErrorText>
        <button type="button" onClick={submit} disabled={save.isPending} className="w-full rounded-full bg-primary py-3 font-semibold text-primary-ink disabled:opacity-50">{edit ? "Save" : "Add to the tree"}</button>
      </div>
    </Sheet>
  );
}
